/**
 * <summary>
 *   This functional component "ChangeAgreementPasswordPage" manages the password settings page for agreements.
 *   It fetches existing password data, determines add/update mode, handles submission, shows success/error toasts,
 *   and displays a warning message if no password is set.
 *   Developer: Sahil Bharti
 * </summary>
 * <returns>Returns JSX</returns>
 */

import React, { useEffect } from "react";
import base64 from "base-64";
import toast from "react-hot-toast";
import { observer } from "mobx-react-lite";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../contexts/store-provider";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import ChangeAgreementPasswordForm from "./add-edit-agreement/forms/agreement-password-settings-form";
import { IUpdateAgreementPassword } from "../../models/forms/IChangePassword";
import { formatMessage } from "../../translations/format-message";
import { getStoreIdFromLocalStorage } from "../../helpers/localstorage-helper";
import { initialStateAgreementPassword } from "../../core/initialState/change-password-state";
import { safeBase64Encode } from "../../constants/constants";
import FormLoader from "../../shared-components/FormLoader/FormLoader";

const ChangeAgreementPasswordPage = () => {
  const { agreementManagementStore } = useStore();
  const {
    UpdateAgreementPasswordService,
    AddAgreementPasswordService,
    GetAgreementPasswordByIdService,
    changeAgreementPasswordState,
    getAgreementPasswordDetail,
    resetChangePassword,
    setDisableAction,
    disableAction
  } = agreementManagementStore;

  const storeId = getStoreIdFromLocalStorage();

  // Fetch the agreement password for the current store when the component mounts
  useEffect(() => {
    resetChangePassword();
    GetAgreementPasswordByIdService(storeId);
    return(()=>{
      setDisableAction(false);
    })
  }, []);

  // Show success toast on password add/update and reset the state afterward
  useEffect(() => {
    if (changeAgreementPasswordState.success) {
      const isUpdateMode = !!getAgreementPasswordDetail?.Password;

      toast.success(
        <FormattedMessage
          id={isUpdateMode ? "updated_success" : "added_success"}
        />
      );

      GetAgreementPasswordByIdService(storeId);
      resetChangePassword();
    }
  }, [changeAgreementPasswordState.success]);

  // Show error toast when password update fails and reset the state afterward
  useEffect(() => {
    if (changeAgreementPasswordState.error) {
      toast.error(<FormattedMessage id={changeAgreementPasswordState.error} />);
    }
    resetChangePassword();
  }, [changeAgreementPasswordState.error]);

  // Submit handler to either add or update agreement password
  const submitHandler = (data: IUpdateAgreementPassword) => {
    const submitData: IUpdateAgreementPassword = {
      Id: getAgreementPasswordDetail?.Id ?? 0,
      StoreId: storeId,
      Password: safeBase64Encode(data.Password),
    };

    if (getAgreementPasswordDetail?.Password) {
      UpdateAgreementPasswordService(submitData);
    } else {
      AddAgreementPasswordService(submitData);
    }
  };

  const isUpdateMode = !!getAgreementPasswordDetail?.Password;

  return (
    <>
      {changeAgreementPasswordState.inProgress && (
        <FormLoader loading={changeAgreementPasswordState.inProgress} />
      )}
      <React.Fragment>
        <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
          <div className="row align-items-center">
            <div className="col-6">
              <div className="page_title_wrap">
                <h1 className="page_title">
                  <FormattedMessage id="password_settings" />
                </h1>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <FormattedMessage id="various_settings" />
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    <FormattedMessage id="password_settings" />
                  </li>
                </ol>
              </div>
            </div>
            <div className="col-6">
              <ul className="add-list">
                <li>
                  <LanguageSlider />
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="container-fluid mt-4 p-0">
          <div className="row align-items-center justify-content-center">
            <div className="col-md-7 col-lg-6 col-xl-4 col-xxl-3 col-12">
              <div className="card">
                <div className="card-header p-3">
                  <h3 className="card-title mb-0">
                    <FormattedMessage id="set_agreement_password" />
                  </h3>
                </div>
                <div className="card-body">
                  <ChangeAgreementPasswordForm
                    id={1}
                    initialValues={{
                      ...initialStateAgreementPassword,
                    }}
                    submitHandler={submitHandler}
                    loading={changeAgreementPasswordState.inProgress}
                    isUpdateMode={isUpdateMode}
                    disableAction={disableAction}
                  />
                </div>
              </div>

              {!getAgreementPasswordDetail?.Password && (
                <div className="alert alert-warning mt-3" role="alert">
                  {formatMessage("no_password_set")}
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    </>
  );
};

export default observer(ChangeAgreementPasswordPage);
